import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Gift, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useClickTracking } from "@/hooks/useClickTracking";
import { useNewsletterContent } from "@/hooks/useNewsletterContent";
import { useTranslation } from "react-i18next";
import { trackNewsletterSignup } from "@/lib/analytics";

interface NewsletterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewsletterModal = ({ open, onOpenChange }: NewsletterModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackClick } = useClickTracking();
  const { content, isLoaded } = useNewsletterContent();

  const newsletterSchema = z.object({
    email: z.string().trim().min(1, t('contact.emailRequired')).email(t('newsletter.invalidEmail')).max(255, t('newsletter.emailTooLong')),
    consent: z.boolean().refine((val) => val === true, t('newsletter.consentRequired')),
  });
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = newsletterSchema.safeParse({ email: email.trim(), consent });
    if (!validation.success) {
      toast({
        title: t('common.error'),
        description: validation.error.errors[0].message,
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    trackClick('newsletter', 'Newsletter Modal Signup', email);

    try {
      const normalizedEmail = email.toLowerCase().trim();

      // Use database function to subscribe or restore
      const { data, error } = await supabase.rpc('subscribe_or_restore_newsletter', {
        subscriber_email: normalizedEmail
      });

      if (error) {
        throw error;
      }

      // Neutral success response — server no longer differentiates statuses
      // to avoid revealing whether an email is already subscribed.
      trackNewsletterSignup(normalizedEmail, 'new');
      toast({
        title: t('newsletter.successTitle') + " 🎉",
        description: t('newsletter.successDescription'),
        duration: 5000,
      });

      setEmail("");
      setConsent(false);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('newsletter.error'),
        description: t('newsletter.errorDescription'),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {open && createPortal(
        <div className="modal-backdrop" onClick={() => onOpenChange(false)} />,
        document.body
      )}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
        ref={contentRef}
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => contentRef.current?.focus());
        }}
        className="sm:max-w-md p-4 sm:p-6 max-h-[90vh] z-[70]"
      >
        <DialogHeader className="space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full gradient-instagram mb-1 sm:mb-3 shadow-glow mx-auto">
            <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <DialogTitle className="text-lg sm:text-2xl font-bold text-center leading-tight">
            {content.newsletter_title} <span className="gradient-text">{content.newsletter_title_accent}</span>
          </DialogTitle>
          <DialogDescription className="text-center text-xs sm:text-sm leading-snug">
            {content.newsletter_description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 sm:gap-3 mt-3 sm:mt-4">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
            </div>
            <span>{content.newsletter_feature_1}</span>
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
            </div>
            <span>{content.newsletter_feature_2}</span>
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
            </div>
            <span>{content.newsletter_feature_3}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-2.5 sm:gap-4 mt-3 sm:mt-6">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={content.newsletter_input_placeholder}
            className="h-10 sm:h-12 px-4 sm:px-6 border-2 focus:border-accent transition-colors"
            disabled={isSubmitting}
            autoFocus={false}
          />

          <div className="flex items-start gap-2">
            <Checkbox
              id="newsletter-modal-consent"
              checked={consent}
              onCheckedChange={(checked) => setConsent(checked === true)}
              disabled={isSubmitting}
              className="mt-0.5 flex-shrink-0"
            />
            <label
              htmlFor="newsletter-modal-consent"
              className="text-[11px] sm:text-xs text-muted-foreground leading-tight cursor-pointer"
            >
              {t('newsletter.consentText')}
            </label>
          </div>

          <Button
            type="submit"
            variant="gradient"
            size="lg"
            className="h-10 sm:h-12 hover:shadow-glow w-full text-sm sm:text-base"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('buttons.subscribing') : content.newsletter_button_text}
          </Button>
        </form>

        <p className="text-[11px] sm:text-xs text-center text-muted-foreground mt-1 sm:mt-2">
          Junte-se a <span className="font-bold gradient-text">{content.newsletter_subscribers_count}</span> {content.newsletter_footer_text}
        </p>
      </DialogContent>
      </Dialog>
    </>
  );
};
